import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from '@emotion/styled';
// https://github.com/import-js/eslint-plugin-import/issues/2266
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';

import useUserContext from '../../hooks/use-user-context';
import useSwiperRef from '../../hooks/use-swiper-ref';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { fontWeights } from '../../theme/type';
import { containerStyles } from '../../styles/layout';
import { dotPatternStyles } from '../../styles/backgrounds';
import { headers, body } from '../../styles/type';

import ButtonArrow from '../buttons/ButtonArrow';
import { ReactComponent as IconQuote } from '../../images/icons/quote.svg';
import TextRenderer from '../TextRenderer';
import VideoPlayer from '../VideoPlayer';
import Background from '../Background';

const StyledBackground = styled(Background)`
  --icon-quote-spacing: ${rem(64)};
  padding: ${rem(32)} 0 ${rem(20)};

  ${media('>tiny')} {
    padding: ${rem(64)} 0 ${rem(20)} 0;
  }

  ${media('>small')} {
    --icon-quote-spacing: ${rem(96)};
  }
`;

const SwiperStyled = styled(Swiper)`
  overflow: visible;
`;

const SlideContentWrapper = styled.div`
  ${containerStyles.regular}
  box-sizing: border-box;

  ${media('>small')} {
    display: flex;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 0 1 ${rem(800)};

  ${media('>tiny')} {
    padding-left: var(--icon-quote-spacing);
  }

  ${media('>small')} {
    padding-right: ${rem(24)};
  }

  ${({ fullWidth }) => fullWidth && `
    flex: 1;
  `}
`;

const Pill = styled.p`
  z-index: 1;
  ${body.small}
  font-weight: ${fontWeights.semiBold};
  background-color: ${colors.blue['800']};
  color: ${colors.white};
  border-radius: ${rem(100)};
  padding: ${rem(4)} ${rem(12)};
  margin-bottom: ${rem(16)};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.24);

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    border: 1px solid ${colors.blue['700']};
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16);
  `}
`;

const Subhead = styled.p`
  ${headers.eyebrow}
  position: relative;
  display: inline-block;
  margin-bottom: ${rem(12)};
  color: ${colors.blue['500']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['200']};
  `}

  &::before {
    ${dotPatternStyles}
    ${({ backgroundPattern }) => backgroundPattern && `
      display: none;
    `}
    content: '';
    position: absolute;
    bottom: 100%;
    margin-bottom: ${rem(24)};
    left: 0;
    width: ${rem(180)};
    height: ${rem(112)};
  }
`;

const Quote = styled.blockquote`
  font-size: ${rem(20)};
  font-weight: ${fontWeights.bold};
  line-height: 1.33;
  position: relative;
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}

  ${media('>tiny')} {
    font-size: ${rem(24)};
  }

  ${media('>mini')} {
    font-size: ${rem(28)};
  }

  ${media('>small')} {
    font-size: ${rem(32)};
  }
`;

const IconQuoteStyled = styled(IconQuote)`
  color: ${colors.blue['500']};
  width: ${rem(32)};

  ${media('>tiny')} {
    position: absolute;
    top: ${rem(2)};
    left: calc(var(--icon-quote-spacing) * -1);
    width: ${rem(36)};
  }

  ${media('>small')} {
    width: ${rem(48)};
  }
`;

const Attribution = styled.p`
  ${body.regular}
  color: ${colors.gray['600']};
  font-weight: ${fontWeights.semiBold};
  margin-top: ${rem(24)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
  `}
`;

const Image = styled.div`
  display: none;
  ${media('>small')} {
    display: block;
    /* margin-top: ${rem(32)}; */
  }
`;

const Video = styled.div`
  margin-top: ${rem(32)};

  ${media('>tiny')} {
    width: 100%;
  }

  ${media('>small')} {
    max-width: ${rem(550)};
    margin: ${rem(32)} auto 0;
  }
`;

const SliderArrows = styled.div`
  display: flex;
  margin: ${rem(-8)};
  padding-top: ${rem(48)};

  ${media('>tiny')} {
    padding-left: var(--icon-quote-spacing);
  }

  ${ButtonArrow} {
    margin: ${rem(8)};
  }
`;

const Testimonials = ({ subhead, testimonialItems, backgroundColor = 'White', backgroundPattern }) => {
  const data = useStaticQuery(graphql`
    query Testiominals {
      allContentfulOptionSet(filter: {name: {eq: "slider"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [prevButton, prevButtonRef] = useSwiperRef();
  const [nextButton, nextButtonRef] = useSwiperRef();

  return (
    <StyledBackground
      color={backgroundColor}
      pattern={backgroundPattern}
      patternVariation={backgroundPattern === 'Large Dots' ? 'swapped' : 'clipped'}
    >
      <SwiperStyled
        autoHeight
        modules={[Navigation, A11y]}
        navigation={{
          prevEl: prevButton,
          nextEl: nextButton
        }}
        loop={testimonialItems.length > 1}
      >
        {testimonialItems.map((testimonial, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <SwiperSlide key={index}>
            <SlideContentWrapper>
              <TextContent fullWidth={!testimonial.image && !testimonial.video}>
                {testimonial.badgeText && (
                  <Pill>
                    <TextRenderer text={testimonial.badgeText} />
                  </Pill>
                )}
                {subhead && (
                  <Subhead backgroundPattern={backgroundPattern}>
                    <TextRenderer text={subhead} />
                  </Subhead>
                )}
                <Quote>
                  <IconQuoteStyled />
                  <TextRenderer text={testimonial.quote?.quote} />
                </Quote>
                <Attribution>
                  <TextRenderer text={testimonial.attribution} />
                </Attribution>
              </TextContent>
              {testimonial.image && !testimonial.video && (
                <Image>
                  <GatsbyImage
                    image={getImage(testimonial.image)}
                    alt={testimonial.image.description}
                    role={!testimonial.image.description ? 'presentation' : null}
                    aria-hidden={!testimonial.image.description ? 'true' : null}
                  />
                </Image>
              )}
              {testimonial.video && (
                <Video>
                  <VideoPlayer
                    url={testimonial.video.videoUrl}
                    title={testimonial.video.title}
                    description={testimonial.video?.description?.description}
                    thumbnailUrl={testimonial.video.thumbnail?.file?.url}
                    thumbnailImage={testimonial.video.thumbnail?.gatsbyImageData}
                    videoBorder={testimonial.video?.videoBorder}
                    uploadDate={testimonial.video.createdAt}
                  />
                </Video>
              )}
            </SlideContentWrapper>
          </SwiperSlide>
        ))}
        {testimonialItems.length > 1 && (
          <ContentWrapper>
            <SliderArrows>
              <ButtonArrow
                ref={prevButtonRef}
                aria-label={options['slider.prev_button_label']}
                left
              />
              <ButtonArrow
                ref={nextButtonRef}
                aria-label={options['slider.next_button_label']}
              />
            </SliderArrows>
          </ContentWrapper>
        )}
      </SwiperStyled>
    </StyledBackground>
  );
};

Testimonials.propTypes = {
  subhead: PropTypes.string,
  testimonialItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      quote: PropTypes.shape({
        quote: PropTypes.string
      }),
      badgeText: PropTypes.string,
      attribution: PropTypes.string,
      image: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        gatsbyImageData: PropTypes.object,
        description: PropTypes.string
      }),
      video: PropTypes.shape({
        videoUrl: PropTypes.string,
        title: PropTypes.string,
        createdAt: PropTypes.string,
        description: PropTypes.shape({
          description: PropTypes.string
        }),
        thumbnail: PropTypes.shape({
          url: PropTypes.string,
          // eslint-disable-next-line react/forbid-prop-types
          gatsbyImageData: PropTypes.object,
          file: PropTypes.shape({
            url: PropTypes.string
          })
        })
      })
    })
  ),
  backgroundColor: PropTypes.oneOf(['White', 'Light Blue']),
  backgroundPattern: PropTypes.oneOf(['Splash Dots', 'Large Dots']),
};

export const query = graphql`
  fragment TestimonialsFragment on ContentfulTestimonials {
    id
    __typename
    subhead
    testimonialItems {
      id
      quote {
        quote
      }
      badgeText
      attribution
      image {
        gatsbyImageData(width: 300, height: 300, quality: 75)
        description
      }
      video {
        videoUrl
        title
        createdAt
        description {
          description
        }
        thumbnail {
          url
          gatsbyImageData(width: 550, height: 310, quality: 75, resizingBehavior: FILL)
          file {
            url
          }
        }
      }
    }
    backgroundColor
    backgroundPattern
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Testimonials;
